export interface SizeChipsProps {
  /** Every size the tag prints, in the order the store prints it. */
  sizes: string[];
  /** The subset of `sizes` that can actually be bought; the rest are struck through. */
  availableSizes: string[];
  /** Sizes covered by the viewer's watch on this product. */
  watchedSizes?: string[];
  /**
   * Supplied only when the viewer can arm a watch. With it the chips are real
   * toggle buttons; without it they are inert print, like the tag they imitate.
   */
  onToggle?: (size: string) => void;
}

/** Sizes are free text off the storefront ("SM", "sm ", "Sm"), so match loosely. */
function key(size: string): string {
  return size.trim().toUpperCase();
}

function toSet(sizes: readonly string[] | undefined): Set<string> {
  const set = new Set<string>();
  for (const size of sizes ?? []) {
    if (typeof size === "string" && size.trim() !== "") set.add(key(size));
  }
  return set;
}

/**
 * Blanks and repeats removed, original order kept — which also makes the size
 * itself a stable React key.
 */
function cleanSizes(sizes: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const size of sizes) {
    if (typeof size !== "string") continue;
    const label = size.trim();
    if (label === "" || seen.has(key(label))) continue;
    seen.add(key(label));
    out.push(label);
  }
  return out;
}

export default function SizeChips({
  sizes,
  availableSizes,
  watchedSizes,
  onToggle,
}: SizeChipsProps) {
  const list = cleanSizes(sizes);
  if (list.length === 0) return null;

  const available = toSet(availableSizes);
  const watched = toSet(watchedSizes);

  return (
    <div className="ap-tag__sizes">
      {list.map((size) => {
        const out = !available.has(key(size));
        const on = watched.has(key(size));
        const className = ["ap-size", out ? "ap-size--out" : null, on ? "ap-size--on" : null]
          .filter(Boolean)
          .join(" ");

        if (!onToggle) {
          return (
            <span key={size} className={className}>
              {size}
              {out && <span className="ap-visually-hidden"> — unavailable</span>}
            </span>
          );
        }

        return (
          <button
            key={size}
            type="button"
            className={className}
            aria-pressed={on}
            aria-label={`Watch size ${size}${out ? " (currently unavailable)" : ""}`}
            onClick={() => onToggle(size)}
          >
            {size}
          </button>
        );
      })}
    </div>
  );
}
